/** The contents of a user's Bio (bio text and images and interests)
 *  in an optionally collapsible disclosure */
import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { PapiEditorText } from '@wix/da-types-papi';
import TextRenderer from '@wix/da-shared-react/pkg/TextRenderer';
import DeviantIdViewer from '@wix/da-shared-react/pkg/DaEditorWrapper/Viewer/DeviantId';
import Separator from '@wix/da-shared-react/pkg/Separator';
import CollapsibleBlock from '@wix/da-shared-react/pkg/CollapsibleBlock';
import Button from '@wix/da-shared-react/pkg/Button';

import Interests from '../Interests';
import { AboutMeMode } from '../types';

import s from './CollapsibleBio.scss';

const COLLAPSE_WHEN_THIS_TALL = 360;

// ----
interface Props {
  // user widget system module and version
  moduleId: number;
  moduleVersion: number;
  // formatted text and images for user's biography
  bio: PapiEditorText;
  // list of interests
  interests?: {
    id: number;
    value: string;
  }[];
}

/** Renders the current bio and interests of a user */
const BioContents: React.FC<Props> = props => {
  const { bio, interests, moduleId, moduleVersion } = props;
  return (
    <>
      <TextRenderer
        textContent={bio}
        legacyTheme="default"
        reactViewer={DeviantIdViewer}
        viewerKey={`bio-${moduleId}-${moduleVersion}`}
      />

      {interests && interests.length > 0 && (
        <>
          <Separator className={s['interests-separator']} />
          <Interests className={s['no']} interests={interests} />
        </>
      )}
    </>
  );
};
BioContents.displayName = 'BioContents';

// ----
/** Renders a user's bio inside a disclosure if over 360px tall */
export const CollapsibleBlockBio: React.FC<Props> = props => {
  const { t } = useTranslation();

  return (
    <CollapsibleBlock
      className={s['bio']}
      maxHeight={COLLAPSE_WHEN_THIS_TALL}
      showFade
      fadeClassName={s['collapsible-fade']}
      renderToggleButton={({ collapsed, onClick }) => (
        <Button
          view="b3"
          className={classnames(
            s['collapsible-button'],
            collapsed && s['collapsed']
          )}
          onClick={onClick}
        >
          {t(collapsed ? 'common.showMore' : 'common.showLess')}
        </Button>
      )}
    >
      <BioContents {...props} />
    </CollapsibleBlock>
  );
};
CollapsibleBlockBio.displayName = 'CollapsibleBlockBio';

// ----
interface BioProps extends Props {
  mode: AboutMeMode;
}

/** Renders a collapsible bio if mode === AboutMeMode.CARD, a plainly wrapped
 *  one otherwise */
export const Bio: React.FC<BioProps> = props => {
  const { mode, ...remainingProps } = props;
  // TODO: this should be done on the parent:
  // {mode === AboutMeMode.CARD ? <Collapsible...}
  // and export the Collapsible and non-collapsible Bio from this module
  // OR as collapsible={mode === AboutMeMode.CARD}
  if (mode === AboutMeMode.CARD) {
    return <CollapsibleBlockBio {...remainingProps} />;
  }

  return (
    <div className={s['bio']}>
      <BioContents {...remainingProps} />
    </div>
  );
};
Bio.displayName = 'Bio';
export default Bio;
